import {useTranslationContext} from 'platform/components';

import {useEffect, useState} from 'react';

import {useDebouncedCallback} from 'shared/web';

import type {EagColumn} from '../../../../../../../../types/EagColumn';
import type {EagFilterProps} from '../../../../../../../../types/EagFilterProps';
import type {FilterState} from '../../../../../../../../types/FilterState';
import {renderFilter} from '../../../../../../../../utils/renderFilter';

const FILTER_DEBOUNCE_DELAY_MS = 250;
const FILTER_DEBOUNCE_MAX_WAIT_MS = 2000;
// Stable empty reference — prevents `?? {}` from creating a new object every render
// which would cause the sync useEffect to fire on every render cycle (React Compiler violation).
const EMPTY_FILTER = {} as FilterState;

type ColumnMenuFilterTabProps = {
  column: EagColumn;
  filterState: FilterState;
  onFilterChange: (key: string, value: unknown) => void;
};

/**
 * Filter tab content for the column header menu.
 *
 * Renders the same filter widget used in FilterPanel and FilterChipPopover,
 * providing a consistent inline editing experience directly from the column header.
 *
 * Uses local state for immediate input responsiveness, with debounced propagation
 * (250ms delay, 2s max wait) to the parent store to avoid a network request on
 * every keystroke while still flushing during continuous typing.
 */
export function ColumnMenuFilterTab(props: ColumnMenuFilterTabProps) {
  const t = useTranslationContext();
  const storeValue = (props.filterState[props.column.key] ?? EMPTY_FILTER) as FilterState;
  const [localValue, setLocalValue] = useState<FilterState>(storeValue);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: sync local filter state from store when external changes occur (preset switch, filter reset)
  useEffect(
    function syncLocalValueFromStore() {
      setLocalValue(storeValue);
    },
    [storeValue]
  );

  const debouncedOnFilterChange = useDebouncedCallback(
    props.onFilterChange,
    FILTER_DEBOUNCE_DELAY_MS,
    FILTER_DEBOUNCE_MAX_WAIT_MS
  );

  const handleChange = (value: FilterState) => {
    setLocalValue(value);
    debouncedOnFilterChange(props.column.key, value);
  };

  const filterProps: EagFilterProps = {
    value: localValue,
    onChange: handleChange,
    isDisabled: false,
    column: props.column,
  };

  if (!props.column.filter) {
    return (
      <div className="p-2 text-xs text-gray-400">
        {t('page.dataGrid.filter.noFilterAvailable', {defaultValue: 'No filter available'})}
      </div>
    );
  }

  return <div className="p-2">{renderFilter(props.column, filterProps)}</div>;
}
